<script setup>
  import { onMounted, onUnmounted, getCurrentInstance } from "vue";
  import { useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import ReportsV2List from "@/components/Reports/ReportV2/ReportsV2List.vue";

  const router = useRouter();
  const instance = getCurrentInstance();
  const cable = instance?.proxy?.$cable;

  const surveyStore = useSurveyStore();
  const { token: surveyToken } = storeToRefs(surveyStore);
  const reportsStore = useReportsV2Store();
  const { searchQuery, loading } = storeToRefs(reportsStore);

  const {
    isOpen: showDeleteDialog,
    target: reportToDelete,
    requestDelete: handleDelete,
    confirm: confirmDelete,
  } = useConfirmDelete(async (report) => {
    try {
      await reportsStore.deleteReport(report.token);
    } catch (error) {
      console.error("Failed to delete report:", error);
    }
  });

  onMounted(() => reportsStore.fetchReports());
  onUnmounted(() => reportsStore.resetFilters());

  function navigateToNew() {
    router.push({ name: "ReportV2New", params: { token: surveyToken.value } });
  }

  function navigateToReport(report) {
    router.push({
      name: "ReportV2Slides",
      params: { token: surveyToken.value, reportToken: report.token },
    });
  }

  async function handleDownload(report) {
    // Fetch the report first to set it as current
    await reportsStore.fetchReport(report.token);
    // Then generate and download
    await reportsStore.generateReport(cable, instance);
  }

  async function handleDuplicate(report) {
    try {
      const duplicated = await reportsStore.duplicateReportFull(report.token);
      if (duplicated) {
        navigateToReport(duplicated);
      }
    } catch (error) {
      console.error("Failed to duplicate report:", error);
    }
  }

</script>

<template>
  <IndexPageLayout
    title="Reports"
    data-cy="survey-reports-page"
    action-label="New report"
    @action="navigateToNew">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search reports" />
    </template>

    <LoadingState v-if="loading" />

    <ReportsV2List
      v-else
      @click="navigateToReport"
      @download="handleDownload"
      @duplicate="handleDuplicate"
      @delete="handleDelete" />

    <template #dialogs>
      <ConfirmDialog
        v-model:open="showDeleteDialog"
        title="Delete Report?"
        confirm-label="Delete"
        @confirm="confirmDelete">
        This will permanently delete "{{ reportToDelete?.name }}" and all its slides. This action cannot be
        undone.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
